import type { ImageItemObject } from '@PosterWhiteboard/items/image-item/image-item.class';
import { ImageItem } from '@PosterWhiteboard/items/image-item/image-item.class';
import { DEFAULT_SLIDE_DURATION } from '@PosterWhiteboard/items/slideshow-item/slideshow-item.types';
import type { SlideshowItem } from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type { Page } from '@PosterWhiteboard/page/page.class';
import type { MediaSlideObject } from '@PosterWhiteboard/items/slideshow-item/media-slide.class';
import { MediaSlide } from '@PosterWhiteboard/items/slideshow-item/media-slide.class';
import type { CopyableItemStyles, InitItemOpts } from '@PosterWhiteboard/items/item/item.types';
import { ITEM_TYPE } from '@PosterWhiteboard/items/item/item.types';
import type { RGBA } from '@Utils/color.util';
import { rgbaToHex } from '@Utils/color.util';
import type {MaskingObject} from '@PosterWhiteboard/classes/masking/masking.types';
import type { ItemFabricObject } from '@PosterWhiteboard/items/item/item.class';
import type { UploadedImageData } from '@Libraries/uploading-image-library';
import {GROWL_TYPE} from '@Libraries/message-growl.types';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type { DeepPartial } from '@/global';
import { getStaticSlideValues } from '@PosterWhiteboard/items/slideshow-item/slideshow.library';
import {removeItemLoading} from '@Libraries/item-loading.library';

const MAX_HIGH_RESOLUTION_DIMENSION = 1600; // Max dimension for high resolution images to prevent browser crashes during generation

export interface ImageSlideItemObject extends ImageItemObject {
  slideDuration: number;
  mediaSlide: MediaSlideObject;
}

export class ImageSlideItem extends ImageItem {
  public override gitype = ITEM_TYPE.IMAGESLIDE;
  public slideDuration = DEFAULT_SLIDE_DURATION;
  public slideshow: SlideshowItem;
  public mediaSlide: MediaSlide;

  public constructor(page: Page, slideshow: SlideshowItem) {
    super(page);
    this.slideshow = slideshow;
    this.mediaSlide = new MediaSlide(this);
  }

  public override toObject(): ImageSlideItemObject {
    return {
      ...super.toObject(),
      ...getStaticSlideValues(this),
      slideDuration: this.slideDuration,
      mediaSlide: this.mediaSlide.toObject(),
    };
  }

  public override copyVals(obj: DeepPartial<ImageSlideItemObject>): void {
    const {mediaSlide, ...itemObj} = obj;
    super.copyVals(itemObj);
    this.mediaSlide.copyVals(mediaSlide);
  }

  public override getParentFabricObject(): ItemFabricObject {
    return this.slideshow.fabricObject;
  }

  protected override getCommonOptions(): Record<string, any> {
    const {left, top, opacity, ...filteredProps} = super.getCommonOptions();
    return filteredProps;
  }

  /**
   * Getter for slideDuration property
   */
  public getSlideDuration(): number {
    return this.slideDuration;
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      slideDuration: this.slideDuration,
      mediaSlide: this.mediaSlide.toObject(),
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<ImageSlideItemObject> = {};
    if (copiedStyles.slideDuration !== undefined) {
      stylesObj.slideDuration = copiedStyles.slideDuration;
    }
    if (copiedStyles.mediaSlide !== undefined) {
      stylesObj.mediaSlide = copiedStyles.mediaSlide;
    }
    await this.updateFromObject(stylesObj, {undoable: false});
    await super.pasteStyles(copiedStyles);
  }

  protected override async reInitFabricObject(): Promise<void> {
    const oldFabricObject = this.fabricObject;
    const slideshowFabricObject = this.slideshow.fabricObject;

    await this.beforeInitFabricObject();
    await this.initFabricObject();
    this.fabricObject.set({left: oldFabricObject.left, top: oldFabricObject.top});
    slideshowFabricObject.remove(oldFabricObject);
    this.slideshow.addSlideToGroupWithOriginalScale(this.fabricObject);
  }

  protected override getTargetMaxDimension(opts: InitItemOpts = {}): number | undefined {
    const baseDimension = super.getTargetMaxDimension(opts);
    if (baseDimension === undefined) {
      return undefined;
    }

    const slideshowScaleX = this.slideshow.fabricObject?.scaleX ?? 1;
    const slideshowScaleY = this.slideshow.fabricObject?.scaleY ?? 1;
    const slideshowScale = Math.max(slideshowScaleX, slideshowScaleY);
    const scaledDimension = baseDimension * slideshowScale;

    // Cap at MAX_HIGH_RESOLUTION_DIMENSION to prevent loading oversized images
    // that can crash the browser's WebGL context during generation
    return Math.min(scaledDimension, MAX_HIGH_RESOLUTION_DIMENSION);
  }

  public getFill(): string {
    return `#${rgbaToHex(this.slideshow.transition.color)}`;
  }

  public override isChildItem(): boolean {
    return true;
  }

  public override getColors(): RGBA[] {
    return super.getColors();
  }

  public getAppliedSidebarProps(): DeepPartial<ImageSlideItemObject> {
    return {
      shadow: this.fabricObject.shadow?.toObject(),
      border: this.border.toObject(),
      effects: this.effects.toObject(),
      mediaSlide: this.mediaSlide.toObject(),
    };
  }

  /** Same rule as the image item: toggling removal clears any strokes painted over the old base image, in the same undo step. */
  public override async enableRemoveBackgroundFlag(): Promise<void> {
    await this.slideshow.updateSlideFromObject(this.uid, {
      removeBackground: {isBackgroundRemoved: true},
      refinement: null,
    });
  }

  public override async disableRemoveBackgroundFlag(): Promise<void> {
    await this.slideshow.updateSlideFromObject(this.uid, {
      removeBackground: {isBackgroundRemoved: false},
      refinement: null,
    });
  }

  public override async updateMasking(maskingObject: MaskingObject | null = null): Promise<void> {
    await this.slideshow.updateSlideFromObject(this.uid, {
      masking: {
        maskingItem: maskingObject,
      },
    });
  }

  protected override async onItemUpdatedFromObject(): Promise<void> {
    this.removeBackground.resumeBackgroundRemovalIfNeeded();
    return Promise.resolve();
  }

  public override async updateImageWithHashedFilenameAndExtension(imageData: UploadedImageData): Promise<void> {
    await this.slideshow.updateSlideFromObject(this.uid, {hashedFilename: imageData.hashedFilename, fileExtension: imageData.fileExtension}, {undoable: false});
    this.slideshow.page.poster.history.recordNonUndoableItemChange(
      {pageId: this.slideshow.page.hashedID, itemId: this.slideshow.uid},
      {slides: {slidesHashMap: {[this.uid]: this.getValuesSetByResolvedUpload()}}}
    );
    removeItemLoading(this);
  }

  public override onUploadingImageFailed(e: Error): void {
    console.error(e);
    getPosterUi().showMessageGrowl({
      key: this.uploadingImageData?.tempUploadingImageUID,
      text: window.i18next.t('pmwjs_image_upload_error'),
      type: GROWL_TYPE.DANGER,
    });
    this.slideshow.deleteSlide(this.uid, false);
  }
}
